import { useMemo, useRef, useState } from 'react';
import { AlertTriangle, Camera, CheckCircle, Copy, Plus, Trash2, Upload } from 'lucide-react';
import { uploadAttachment, planApiErrorMessage } from '@/utils/planApi';

// ---------------------------------------------------------------------------
// ProofTestLog (blueprint v2 C-4b) — REPEATABLE §9.6 proof-test documentation.
// Keyed to (riggingScenarioId, location): a NEW record is required every time
// the rigging MOVES TO A NEW LOCATION (mast pipe 1→2→3 on the same sector =
// new location, same scenario — §9.6(i)). Per location: ONE proof test, then
// UNLIMITED lifts. A scenario CHANGE (tag method differs) = new rigging
// scenario in the plan.
//
// Per record: config label, test load (pre-filled 1.25 × max anticipated
// lifted load — VERIFIED), hold ≥ 5 min (VERIFIED), device (Dillon tension
// meter / calibrated load cell + device ID), evidence photo pairs
// (time-date-stamped 125%-load shot + second shot ≥ 5 min later — EXIF
// timestamps displayed + user-confirmed, uploaded via the plan attachment
// route with type='proof-test'), attestation "no personnel on structure
// during test", post-test inspection by qualified person (picker), notes.
// Status: pending → documented (photos + attestation complete).
// ---------------------------------------------------------------------------

export interface ProofTestPhoto {
  /** Stored attachment name/url once uploaded (or object URL pre-upload). */
  url: string;
  name: string;
  role: 'load-125' | 'hold-5min' | 'extra';
  exifTimestamp: string | null;   // EXIF DateTimeOriginal when present
  exifConfirmedByUser: boolean;
  uploadedAt: string | null;
}

export interface ProofTestRecord {
  id: string;
  riggingScenarioId: string;      // plan scenario the config belongs to
  location: string;               // e.g. "Mast pipe 2 — sector A" (new record per move)
  configLabel: string;            // rigging-configuration label
  maxAnticipatedLoadLbs: number | null;
  testLoadLbs: number | null;     // pre-filled 1.25 × max anticipated (VERIFIED)
  holdMinutes: number;            // ≥ 5 required (VERIFIED)
  device: string;                 // e.g. "Dillon tension meter" / "calibrated load cell"
  deviceId: string;
  photos: ProofTestPhoto[];
  attestationNoPersonnel: boolean;
  postTestInspector: string | null;
  notes: string;
  status: 'pending' | 'documented';
  createdAt: string;
  documentedAt: string | null;
}

export interface ProofTestLogProps {
  records: ProofTestRecord[];
  onRecordsChange: (records: ProofTestRecord[]) => void;
  /** Saved plan id — required for photo uploads through the attachment route. */
  planId: number | null;
  qpName: string;
  inspectors: { id: number; name: string; role: string }[];
  scenarios: { id: string; label: string }[];
  onUploadError?: (message: string) => void;
}

let recordCounter = 0;
function newRecordId(): string {
  recordCounter += 1;
  return `proof-test-${Date.now()}-${recordCounter}`;
}

/** Pre-fill 1.25 × max anticipated lifted load (VERIFIED §9.6 parameter). */
export function suggestedTestLoad(maxAnticipatedLoadLbs: number | null): number | null {
  return maxAnticipatedLoadLbs !== null && maxAnticipatedLoadLbs > 0
    ? Math.round(maxAnticipatedLoadLbs * 1.25 * 10) / 10
    : null;
}

/**
 * Minimal JPEG EXIF DateTimeOriginal reader (no new deps): scans APP1/Exif for
 * tag 0x9003. Returns ISO-ish local string or null. Best-effort — the user
 * confirms the displayed timestamp either way.
 */
export async function readExifTimestamp(file: File): Promise<string | null> {
  try {
    const buf = new Uint8Array(await file.slice(0, 128 * 1024).arrayBuffer());
    if (buf[0] !== 0xff || buf[1] !== 0xd8) return null;
    let off = 2;
    while (off + 4 < buf.length) {
      if (buf[off] !== 0xff) break;
      const marker = buf[off + 1];
      const size = (buf[off + 2] << 8) | buf[off + 3];
      if (marker === 0xe1) {
        // "Exif\0\0" then TIFF header
        const tiff = off + 10;
        if (tiff + 8 > buf.length) return null;
        const little = buf[tiff] === 0x49 && buf[tiff + 1] === 0x49;
        const u16 = (p: number) => (little ? buf[p] | (buf[p + 1] << 8) : (buf[p] << 8) | buf[p + 1]);
        const u32 = (p: number) =>
          little
            ? buf[p] | (buf[p + 1] << 8) | (buf[p + 2] << 16) | (buf[p + 3] << 24)
            : (buf[p] << 24) | (buf[p + 1] << 16) | (buf[p + 2] << 8) | buf[p + 3];
        const ifd0 = tiff + u32(tiff + 4);
        const findInIfd = (ifdOff: number, target: number, nextIfdTag: number): number | null => {
          if (ifdOff + 2 > buf.length) return null;
          const count = u16(ifdOff);
          for (let i = 0; i < count; i += 1) {
            const entry = ifdOff + 2 + i * 12;
            if (entry + 12 > buf.length) return null;
            if (u16(entry) === target) return u32(entry + 8);
          }
          if (nextIfdTag) {
            const next = u32(ifdOff + 2 + count * 12);
            if (next > 0 && next + 2 < buf.length - tiff) return findInIfd(tiff + next, target, 0);
          }
          return null;
        };
        // 0x9003 DateTimeOriginal lives in the Exif sub-IFD (0x8769 pointer).
        const exifIfdPtr = findInIfd(ifd0, 0x8769, 0);
        let valueOff: number | null = null;
        if (exifIfdPtr !== null) valueOff = findInIfd(tiff + exifIfdPtr, 0x9003, 0);
        if (valueOff === null) valueOff = findInIfd(ifd0, 0x0132, 0); // fallback: DateTime
        if (valueOff === null) return null;
        const strOff = tiff + valueOff;
        let s = '';
        for (let p = strOff; p < strOff + 19 && p < buf.length; p += 1) {
          if (buf[p] === 0) break;
          s += String.fromCharCode(buf[p]);
        }
        // "YYYY:MM:DD HH:MM:SS"
        if (/^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) {
          return s.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3');
        }
        return null;
      }
      off += 2 + size;
    }
    return null;
  } catch {
    return null;
  }
}

function computeStatus(r: ProofTestRecord): ProofTestRecord['status'] {
  const hasLoadPhoto = r.photos.some((p) => p.role === 'load-125' && p.exifConfirmedByUser);
  const hasHoldPhoto = r.photos.some((p) => p.role === 'hold-5min' && p.exifConfirmedByUser);
  const holdOk = r.holdMinutes >= 5;
  const loadOk = r.testLoadLbs !== null && r.testLoadLbs > 0;
  return hasLoadPhoto && hasHoldPhoto && holdOk && loadOk && r.attestationNoPersonnel && !!r.postTestInspector
    ? 'documented'
    : 'pending';
}

export function ProofTestLog({
  records,
  onRecordsChange,
  planId,
  qpName,
  inspectors,
  scenarios,
  onUploadError,
}: ProofTestLogProps) {
  const [uploading, setUploading] = useState<string | null>(null);
  const fileInputs = useRef<Record<string, HTMLInputElement | null>>({});

  const update = (id: string, patch: Partial<ProofTestRecord>) => {
    onRecordsChange(
      records.map((r) => {
        if (r.id !== id) return r;
        const next = { ...r, ...patch };
        next.status = computeStatus(next);
        next.documentedAt = next.status === 'documented' ? r.documentedAt ?? new Date().toISOString() : null;
        return next;
      })
    );
  };

  const addRecord = (preset?: Partial<ProofTestRecord>) => {
    const now = new Date().toISOString();
    const maxLoad = preset?.maxAnticipatedLoadLbs ?? null;
    const record: ProofTestRecord = {
      id: newRecordId(),
      riggingScenarioId: preset?.riggingScenarioId ?? scenarios[0]?.id ?? 'scenario-1',
      location: preset?.location ?? '',
      configLabel: preset?.configLabel ?? '',
      maxAnticipatedLoadLbs: maxLoad,
      testLoadLbs: preset?.testLoadLbs ?? suggestedTestLoad(maxLoad),
      holdMinutes: preset?.holdMinutes ?? 5,
      device: preset?.device ?? '',
      deviceId: preset?.deviceId ?? '',
      photos: [],
      attestationNoPersonnel: false,
      postTestInspector: preset?.postTestInspector ?? null,
      notes: preset?.notes ?? '',
      status: 'pending',
      createdAt: now,
      documentedAt: null,
    };
    onRecordsChange([...records, record]);
  };

  /** 'Duplicate last proof test config to new location' — copies config, clears evidence. */
  const duplicateLast = () => {
    const last = records[records.length - 1];
    if (!last) {
      addRecord();
      return;
    }
    addRecord({
      riggingScenarioId: last.riggingScenarioId,
      configLabel: last.configLabel,
      maxAnticipatedLoadLbs: last.maxAnticipatedLoadLbs,
      testLoadLbs: suggestedTestLoad(last.maxAnticipatedLoadLbs),
      holdMinutes: last.holdMinutes,
      device: last.device,
      deviceId: last.deviceId,
      notes: last.notes,
      // Evidence is NEVER duplicated — new location = new photos + timestamps.
    });
  };

  const addPhoto = async (recordId: string, file: File, role: ProofTestPhoto['role']) => {
    const record = records.find((r) => r.id === recordId);
    if (!record) return;
    setUploading(recordId);
    const exif = await readExifTimestamp(file);
    const photo: ProofTestPhoto = {
      url: URL.createObjectURL(file),
      name: file.name,
      role,
      exifTimestamp: exif,
      exifConfirmedByUser: false,
      uploadedAt: null,
    };
    try {
      if (planId) {
        // Upload via the existing plan attachment route, type='proof-test'.
        await uploadAttachment(planId, file, 'proof-test', {
          proofTestRecordId: recordId,
          photoRole: role,
          exifTimestamp: exif ?? undefined,
        });
        photo.uploadedAt = new Date().toISOString();
      }
    } catch (error) {
      onUploadError?.(planApiErrorMessage(error, 'Proof-test photo upload failed — saved locally in the plan draft.'));
    }
    update(recordId, { photos: [...record.photos, photo] });
    setUploading(null);
  };

  const removePhoto = (recordId: string, photoIndex: number) => {
    const record = records.find((r) => r.id === recordId);
    if (!record) return;
    update(recordId, { photos: record.photos.filter((_, i) => i !== photoIndex) });
  };

  const grouped = useMemo(
    () =>
      records.reduce<Record<string, ProofTestRecord[]>>((acc, r) => {
        const key = `${r.riggingScenarioId} @ ${r.location || '(no location)'}`;
        (acc[key] ||= []).push(r);
        return acc;
      }, {}),
    [records]
  );

  return (
    <div className="space-y-4" data-testid="proof-test-log">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h4 className="font-bold text-gray-900">§9.6 Proof Test Log</h4>
          <p className="text-xs text-gray-500 max-w-2xl">
            One record per rigging configuration AND location — a new proof test is required every time the
            rigging moves to a new location (mast 1 → 2 → 3, or a different mount), even when the scenario is
            unchanged (§9.6(i)). Per location: one test, then unlimited lifts. Field workflow: suspend 125% →
            photo → wait 5 min → photo → inspect → log.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => addRecord()}
            className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm flex items-center gap-1"
          >
            <Plus className="h-4 w-4" /> New proof test
          </button>
          <button
            type="button"
            onClick={duplicateLast}
            className="px-3 py-1.5 border border-gray-300 rounded text-sm text-gray-700 flex items-center gap-1"
          >
            <Copy className="h-4 w-4" /> Duplicate last proof test config to new location
          </button>
        </div>
      </div>

      {records.length === 0 && (
        <p className="text-sm text-gray-500 italic">
          No proof-test records yet. Add one when the §4.4.4.1 exception condition iv (or any §9.6 trigger)
          applies.
        </p>
      )}

      {Object.entries(grouped).map(([key, recs]) => (
        <div key={key}>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">{key}</p>
          <div className="space-y-3">
            {recs.map((r) => (
              <div
                key={r.id}
                className={`border rounded-lg p-4 ${r.status === 'documented' ? 'border-green-300 bg-green-50/40' : 'border-gray-300 bg-white'}`}
              >
                <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-bold ${r.status === 'documented' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}
                  >
                    {r.status === 'documented' ? 'DOCUMENTED' : 'PENDING'}
                  </span>
                  <button
                    type="button"
                    onClick={() => onRecordsChange(records.filter((x) => x.id !== r.id))}
                    className="text-gray-400 hover:text-red-600"
                    aria-label="Remove proof test record"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Rigging scenario</label>
                    <select
                      value={r.riggingScenarioId}
                      onChange={(e) => update(r.id, { riggingScenarioId: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      {scenarios.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                      {!scenarios.some((s) => s.id === r.riggingScenarioId) && (
                        <option value={r.riggingScenarioId}>{r.riggingScenarioId}</option>
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Location (new record per move)
                    </label>
                    <input
                      type="text"
                      value={r.location}
                      onChange={(e) => update(r.id, { location: e.target.value })}
                      placeholder="e.g. Mast pipe 2 — sector A"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Config label</label>
                    <input
                      type="text"
                      value={r.configLabel}
                      onChange={(e) => update(r.id, { configLabel: e.target.value })}
                      placeholder="e.g. Gin pole + 2-part hoist"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Max anticipated lifted load (lb)
                    </label>
                    <input
                      type="number"
                      value={r.maxAnticipatedLoadLbs ?? ''}
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : Number(e.target.value);
                        update(r.id, { maxAnticipatedLoadLbs: v, testLoadLbs: suggestedTestLoad(v) });
                      }}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Test load (lb) — pre-filled 1.25 ×
                    </label>
                    <input
                      type="number"
                      value={r.testLoadLbs ?? ''}
                      onChange={(e) =>
                        update(r.id, { testLoadLbs: e.target.value === '' ? null : Number(e.target.value) })
                      }
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Hold time (min, ≥5)</label>
                    <input
                      type="number"
                      min={5}
                      value={r.holdMinutes}
                      onChange={(e) => update(r.id, { holdMinutes: Number(e.target.value) })}
                      className={`w-full px-2 py-1.5 border rounded text-sm ${r.holdMinutes >= 5 ? 'border-gray-300' : 'border-red-400'}`}
                    />
                    {r.holdMinutes < 5 && (
                      <p className="text-xs text-red-600 mt-0.5">§9.6 requires holding ≥ 5 minutes.</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Device</label>
                    <input
                      type="text"
                      value={r.device}
                      onChange={(e) => update(r.id, { device: e.target.value })}
                      placeholder="Dillon tension meter / calibrated load cell"
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Device ID / serial</label>
                    <input
                      type="text"
                      value={r.deviceId}
                      onChange={(e) => update(r.id, { deviceId: e.target.value })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Post-test inspection by (qualified person)
                    </label>
                    <select
                      value={r.postTestInspector ?? ''}
                      onChange={(e) => update(r.id, { postTestInspector: e.target.value || null })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                    >
                      <option value="">— select inspector —</option>
                      {inspectors.map((p) => (
                        <option key={p.id} value={p.name}>
                          {p.name} {p.role ? `(${p.role})` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Evidence photos */}
                <div className="mt-3">
                  <p className="text-xs font-semibold text-gray-700 flex items-center gap-1">
                    <Camera className="h-4 w-4" /> Evidence photos (2+ required: 125%-load shot + shot ≥5 min later)
                  </p>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {(
                      [
                        ['load-125', 'Add 125%-load photo'],
                        ['hold-5min', 'Add ≥5-min hold photo'],
                        ['extra', 'Add extra photo'],
                      ] as [ProofTestPhoto['role'], string][]
                    ).map(([role, label]) => (
                      <span key={role}>
                        <input
                          ref={(el) => {
                            fileInputs.current[`${r.id}-${role}`] = el;
                          }}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void addPhoto(r.id, file, role);
                            e.target.value = '';
                          }}
                        />
                        <button
                          type="button"
                          disabled={uploading === r.id}
                          onClick={() => fileInputs.current[`${r.id}-${role}`]?.click()}
                          className="px-2 py-1 border border-gray-300 rounded text-xs text-gray-700 flex items-center gap-1 disabled:opacity-50"
                        >
                          <Upload className="h-3 w-3" /> {label}
                        </button>
                      </span>
                    ))}
                  </div>
                  {r.photos.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {r.photos.map((p, i) => (
                        <li key={`${p.name}-${i}`} className="text-xs text-gray-700 flex flex-wrap items-center gap-2">
                          <span className="font-medium">{p.role}:</span> {p.name}
                          <span className="text-gray-500">
                            EXIF: {p.exifTimestamp ? p.exifTimestamp : 'not found (camera timestamp unavailable)'}
                            {p.uploadedAt ? ' · uploaded' : planId ? ' · upload pending' : ' · stored with plan on save'}
                          </span>
                          <label className="flex items-center gap-1">
                            <input
                              type="checkbox"
                              checked={p.exifConfirmedByUser}
                              onChange={(e) => {
                                const photos = [...r.photos];
                                photos[i] = { ...p, exifConfirmedByUser: e.target.checked };
                                update(r.id, { photos });
                              }}
                            />
                            Timestamp confirmed
                          </label>
                          <button
                            type="button"
                            onClick={() => removePhoto(r.id, i)}
                            className="text-gray-400 hover:text-red-600"
                            aria-label="Remove photo"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {(() => {
                    const load = r.photos.find((p) => p.role === 'load-125');
                    const hold = r.photos.find((p) => p.role === 'hold-5min');
                    if (load?.exifTimestamp && hold?.exifTimestamp) {
                      const deltaMin =
                        (new Date(hold.exifTimestamp).getTime() - new Date(load.exifTimestamp).getTime()) / 60000;
                      return deltaMin < 5 ? (
                        <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" /> Photo timestamps are only {deltaMin.toFixed(1)} min
                          apart — the hold photo must be ≥ 5 minutes after the load photo.
                        </p>
                      ) : (
                        <p className="mt-1 text-xs text-green-700 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3" /> Hold gap verified: {deltaMin.toFixed(1)} min ≥ 5 min.
                        </p>
                      );
                    }
                    return null;
                  })()}
                </div>

                {/* Attestation + notes */}
                <div className="mt-3 space-y-2">
                  <label className="flex items-start gap-2 text-sm text-gray-800">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={r.attestationNoPersonnel}
                      onChange={(e) => update(r.id, { attestationNoPersonnel: e.target.checked })}
                    />
                    Attestation: no personnel were on the structure during the proof test.
                  </label>
                  <textarea
                    value={r.notes}
                    onChange={(e) => update(r.id, { notes: e.target.value })}
                    placeholder="Notes"
                    rows={2}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm"
                  />
                  <p className="text-[11px] text-gray-500">
                    Created {new Date(r.createdAt).toLocaleString()}
                    {r.documentedAt ? ` · documented ${new Date(r.documentedAt).toLocaleString()}` : ''}
                    {qpName ? ` · QP of record: ${qpName}` : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
